import React, { useEffect, useRef } from "react";
import SVGA from "svgaplayerweb";
import "./FemaleLevelFrame.css";

const FEMALE_LEVEL_FRAMES = {
  1: "/frames/female_lv_1.svga",
  2: "/frames/female_lv_2.svga",
  3: "/frames/female_lv_3.svga",
  4: "/frames/female_lv_4.svga",
  5: "/frames/female_lv_5.svga",
  6: "/frames/female_lv_6.svga",
  7: "/frames/female_lv_7.svga",
  8: "/frames/female_lv_8.svga",
  9: "/frames/female_lv_9.svga",
  10: "/frames/female_lv_10.svga",
};

export const FEMALE_FRAME_CALIBRATION = {
  1: {
    frameSlot: 1.42,
    frameScale: 0.98,
    frameX: 25.4,
    frameY: 27,
    avatarInset: "22%",
    photoScale: 1.02,
    badgeX: 20,
    badgeY: 6,
    badgeScale: 0.70,
    badgeWidth: "26%",
  },
  2: {
    frameSlot: 1.48,
    frameScale: 0.98,
    frameX: 23,
    frameY: 24,
    avatarInset: "22%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 4,
    badgeScale: 0.75,
    badgeWidth: "26%",
  },
  3: {
    frameSlot: 1.48,
    frameScale: 0.98,
    frameX: 23,
    frameY: 23,
    avatarInset: "25%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 6,
    badgeScale: 0.75,
    badgeWidth: "26%",
  },
  4: {
    frameSlot: 1.42,
    frameScale: 0.98,
    frameX: 25,
    frameY: 27,
    avatarInset: "22%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 5,
    badgeScale: 0.75,
    badgeWidth: "26%",
  },
  5: {
    frameSlot: 1.42,
    frameScale: 0.98,
    frameX: 25.4,
    frameY: 25,
    avatarInset: "22%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 5,
    badgeScale: 0.75,
    badgeWidth: "26%",
  },
  6: {
    frameSlot: 1.82,
    frameScale: 1,
    frameX: 10,
    frameY: 11,
    avatarInset: "25%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 5,
    badgeScale: 0.75,
    badgeWidth: "26%",
  },
  7: {
    frameSlot: 1.42,
    frameScale: 0.98,
    frameX: 25,
    frameY: 27,
    avatarInset: "22%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 5,
    badgeScale: 0.75,
    badgeWidth: "26%",
  },
  8: {
    frameSlot: 1.42,
    frameScale: 0.98,
    frameX: 25,
    frameY: 27,
    avatarInset: "22%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 5,
    badgeScale: 0.75,
    badgeWidth: "26%",
  },
  9: {
    frameSlot: 1.42,
    frameScale: 0.98,
    frameX: 25,
    frameY: 27,
    avatarInset: "22%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 5,
    badgeScale: 0.75,
    badgeWidth: "26%",
  },
  10: {
    frameSlot: 1.48,
    frameScale: 0.98,
    frameX: 23,
    frameY: 26,
    avatarInset: "22%",
    photoScale: 1.02,
    badgeX: 15,
    badgeY: 5,
    badgeScale: 0.80,
    badgeWidth: "27%",
    badgeFontScale: 0.75,
  },
};

export default function FemaleLevelFrame({
  level = 0,
  imageUrl = "",
  size = 170,
  className = "",
}) {
  const hostRef = useRef(null);

  const safeLevel = Math.max(0, Math.min(10, Number(level) || 0));
  const frameUrl = safeLevel === 0 ? "" : FEMALE_LEVEL_FRAMES[safeLevel] || "";
  const frameCalib =
    FEMALE_FRAME_CALIBRATION[safeLevel] || FEMALE_FRAME_CALIBRATION[6];

  useEffect(() => {
    if (!frameUrl || !hostRef.current) return;

    let mounted = true;
    let player = null;
    let parser = null;

    const cleanup = () => {
      try {
        if (player) {
          try {
            player.stopAnimation?.();
          } catch (_) {}
          try {
            player.clear?.();
          } catch (_) {}
          try {
            player.destroy?.();
          } catch (_) {}
        }
      } catch (_) {}
    };

    try {
      player = new SVGA.Player(hostRef.current);
      parser = new SVGA.Parser();

      player.loops = 0;
      player.clearsAfterStop = false;
      player.fillMode = "Forward";

      parser.load(
        frameUrl,
        (videoItem) => {
          if (!mounted || !videoItem) return;
          player.setVideoItem(videoItem);
          player.setContentMode("AspectFit");
          player.startAnimation();
        },
        (err) => {
          console.error("SVGA load error:", err);
          cleanup();
        }
      );
    } catch (err) {
      console.error("SVGA init error:", err);
      cleanup();
    }

    return () => {
      mounted = false;
      cleanup();
    };
  }, [frameUrl]);

  return (
    <div
      className={`flf-root level-${safeLevel} ${className}`}
      style={{
        "--frame-size": `${size}px`,
        "--flf-frame-slot": String(frameCalib.frameSlot),
        "--flf-frame-scale": String(frameCalib.frameScale),
        "--flf-frame-x": `${frameCalib.frameX}%`,
        "--flf-frame-y": `${frameCalib.frameY}%`,
        "--flf-avatar-inset": frameCalib.avatarInset,
        "--flf-photo-scale": String(frameCalib.photoScale),
        "--flf-badge-x": `${frameCalib.badgeX}px`,
        "--flf-badge-y": `${frameCalib.badgeY}px`,
        "--flf-badge-scale": String(frameCalib.badgeScale),
        "--flf-badge-width": frameCalib.badgeWidth,
        "--flf-badge-font-scale": String(frameCalib.badgeFontScale || 1),
      }}
    >
      {frameUrl ? <div ref={hostRef} className="flf-svga-layer" /> : null}

      <div className={`flf-core ${safeLevel === 0 ? "no-frame" : ""}`}>
        <div className="flf-photo-shell">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="flf-photo" />
          ) : (
            <div className="flf-photo-placeholder" />
          )}
        </div>
      </div>

      {safeLevel > 0 && (
        <div className="flf-badge">
          <div className="flf-badge-inner">
            <span>Lv {safeLevel}</span>
          </div>
        </div>
      )}
    </div>
  );
}